// src/config/taskStyles.ts
// Task 相關的樣式配置常量和工具函數

import { TaskPriority, TaskEnergyLevel, TaskCategory } from '../types/task';
import { TimeBoxCategory } from '../types/timeBox';

// ============================================
// 類型定義
// ============================================

export type CategoryType = 'work' | 'personal' | 'family' | 'health' | 'social' | 'default';
export type StyleType = 'border' | 'badge' | 'bg';

interface CategoryStyleMap {
  [key: string]: string;
  work: string;
  personal: string;
  family: string;
  health: string;
  social: string;
  default: string;
}

interface CategoryStyles {
  border: CategoryStyleMap;
  badge: CategoryStyleMap;
  bg: CategoryStyleMap;
}

// ============================================
// 類別樣式映射
// ============================================

export const CATEGORY_STYLES: CategoryStyles = {
  border: {
    work: 'border-sky-500',
    personal: 'border-yellow-500',
    family: 'border-green-500',
    health: 'border-sky-500',
    social: 'border-pink-500',
    default: 'border-gray-400'
  },
  badge: {
    work: 'bg-sky-100 text-sky-800',
    personal: 'bg-yellow-100 text-yellow-800',
    family: 'bg-green-100 text-green-800',
    health: 'bg-sky-100 text-sky-800',
    social: 'bg-pink-100 text-pink-800',
    default: 'bg-gray-100 text-gray-800'
  },
  bg: {
    work: 'bg-sky-50',
    personal: 'bg-yellow-50',
    family: 'bg-green-50',
    health: 'bg-sky-50',
    social: 'bg-pink-50',
    default: 'bg-gray-50'
  }
};

// ============================================
// 優先級樣式
// ============================================

export const PRIORITY_STYLES: Record<TaskPriority, string> = {
  '1': 'bg-red-100 text-red-800',
  '2': 'bg-yellow-100 text-yellow-800',
  '3': 'bg-sky-100 text-sky-800'
};

export const PRIORITY_MAP: Record<TaskPriority, string> = {
  '1': 'High',
  '2': 'Medium',
  '3': 'Low'
};

// ============================================
// 能量等級樣式
// ============================================

export const ENERGY_STYLES: Record<TaskEnergyLevel, string> = {
  '1': 'bg-red-100 text-red-800',
  '2': 'bg-yellow-100 text-yellow-800',
  '3': 'bg-green-100 text-green-800'
};

export const ENERGY_MAP: Record<TaskEnergyLevel, string> = {
  '1': 'High',
  '2': 'Medium',
  '3': 'Low'
};

// ============================================
// Task Category → TimeBox Category 映射
// ============================================

export const TASK_TO_TIMEBOX_CATEGORY: Record<TaskCategory | 'null', TimeBoxCategory> = {
  work: 'focus',
  family: 'life',
  health: 'life',
  social: 'life',
  personal: 'life',
  null: 'focus'
};

// ============================================
// 工具函數
// ============================================

/**
 * 根據類別獲取對應樣式
 */
export const getCategoryStyle = (
  category: string | undefined | null,
  type: StyleType
): string => {
  if (!category) return CATEGORY_STYLES[type].default;
  return (category in CATEGORY_STYLES[type])
    ? CATEGORY_STYLES[type][category]
    : CATEGORY_STYLES[type].default;
};

/**
 * 獲取優先級標籤樣式
 */
export const getPriorityBadgeStyle = (priority: TaskPriority | undefined): string => {
  return priority ? PRIORITY_STYLES[priority] : '';
};

/**
 * 獲取優先級顯示文字
 */
export const getPriorityLabel = (priority: TaskPriority | undefined): string => {
  return priority ? PRIORITY_MAP[priority] : '';
};

/**
 * 獲取能量等級標籤樣式
 */
export const getEnergyBadgeStyle = (energy: TaskEnergyLevel | undefined): string => {
  return energy ? ENERGY_STYLES[energy] : '';
};

/**
 * 獲取能量等級顯示文字
 */
export const getEnergyLabel = (energy: TaskEnergyLevel | undefined): string => {
  return energy ? ENERGY_MAP[energy] : '';
};

/**
 * 根據 Task 類別獲取對應的 TimeBox 類別
 */
export const getTimeBoxCategory = (taskCategory: TaskCategory | undefined | null): TimeBoxCategory => {
  if (!taskCategory) return 'focus';
  return TASK_TO_TIMEBOX_CATEGORY[taskCategory] || 'focus';
};
